import { Link } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassSwatch } from "@/components/learn/masterclass-swatch"
import { Badge } from "@/components/ui/badge"
import { masterclassPath } from "@/routes"
import type { CompletedMasterclassItem } from "@/types/crew"

/**
 * OKS-57 — Single item in the per-creator Masterclasses-completed list.
 *
 * Whole row is an Inertia `<Link>` to `masterclassPath(item.id)`. The
 * "Unpublished" badge surfaces when the masterclass was unpublished
 * after the creator completed it (`state="draft"`) — **the row stays
 * linked regardless** (TRD KD-8 — brand admin can still view the brand-
 * side Show page for an unpublished masterclass; only creators 404).
 *
 * Visual primitives:
 *  - Left thumbnail: shared `<MasterclassSwatch size="thumb">` (reused
 *    from OKS-56). Deterministic-from-id 6-tone palette + GraduationCap
 *    glyph; rounded square.
 *  - Middle: title (1-line clamp) + meta line "topic · Completed Mon DD,
 *    YYYY" (`text-xs muted`). Unpublished badge inline next to title.
 *  - Right: muted `ChevronRight` glyph (hover-emphasised).
 */
export interface CreatorCompletedMasterclassesItemProps {
  item: CompletedMasterclassItem
}

export function CreatorCompletedMasterclassesItem({
  item,
}: CreatorCompletedMasterclassesItemProps) {
  const { t, i18n } = useTranslation()

  const isUnpublished = item.state === "draft"

  const locale = i18n.language === "en" ? "en-US" : "fr-FR"
  const completedDate = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(item.completed_at))

  return (
    <li data-testid={`creator-completed-masterclass-${item.id}`}>
      <Link
        href={masterclassPath(item.id)}
        className="focus-visible:ring-ring/50 hover:bg-muted/50 group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <MasterclassSwatch id={item.id} size="thumb" className="shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span
              className="text-foreground line-clamp-1 text-sm font-semibold"
              data-testid={`creator-completed-masterclass-${item.id}-title`}
            >
              {item.title}
            </span>
            {isUnpublished && (
              <Badge
                variant="secondary"
                data-testid={`creator-completed-masterclass-${item.id}-unpublished-badge`}
              >
                {t("crew.detail.masterclasses.unpublished_badge")}
              </Badge>
            )}
          </div>
          <span className="text-muted-foreground line-clamp-1 text-xs">
            {item.topic} ·{" "}
            <span
              data-testid={`creator-completed-masterclass-${item.id}-completed-at`}
            >
              {t("crew.detail.masterclasses.item_completed_at", {
                date: completedDate,
              })}
            </span>
          </span>
        </div>
        <ChevronRight
          aria-hidden="true"
          className="text-muted-foreground group-hover:text-foreground size-4 shrink-0 transition-colors"
        />
      </Link>
    </li>
  )
}
